import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAccount, useSwitchChain } from "wagmi";

import { toaster } from "@/components/ui/toaster";
import { AppRoute, plumeMainnetId } from "@/constants/shared";

/* this hook is used to auto-switch to the Plume mainnet network 
   on page navigation within the hub if the user is not on the Plume mainnet network 
   except for the bridge page.
   There's some extra logic to prevent infinite loops of switching networks.
 */
export const useNetworkGuard = () => {
  const { chain, isConnected } = useAccount();
  const { switchChain } = useSwitchChain();
  const location = useLocation();
  const switchInProgress = useRef(false);
  const lastProcessedChain = useRef<number | null>(null);
  const lastProcessedPath = useRef<string>("");

  useEffect(() => {
    // Reset when path changes
    if (location.pathname !== lastProcessedPath.current) {
      switchInProgress.current = false;
      lastProcessedChain.current = null;
      lastProcessedPath.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    // Prevent processing if already in progress or already processed this chain
    if (
      !isConnected ||
      !chain ||
      switchInProgress.current ||
      lastProcessedChain.current === chain.id
    ) {
      return;
    }

    const isBridgePage = location.pathname.includes(AppRoute.Bridge);
    const isOnPlume = chain.id === plumeMainnetId;

    // Only auto-switch outside bridge page and when not on Plume
    if (!isBridgePage && !isOnPlume) {
      switchInProgress.current = true;
      lastProcessedChain.current = chain.id;

      // Use setTimeout to prevent blocking the render cycle
      setTimeout(async () => {
        try {
          await switchChain({ chainId: plumeMainnetId });
        } catch (error) {
          console.error("Network switch failed:", error);
          switchInProgress.current = false;

          toaster.create({
            title: "Please switch to Plume network",
            description:
              "To use Plume Portal, you must be on the Plume mainnet network.",
            type: "warning",
            duration: 8000,
            action: {
              label: "Switch Network",
              onClick: () => {
                try {
                  switchChain({ chainId: plumeMainnetId });
                } catch {
                  toaster.create({
                    title: "Unable to switch networks",
                    description:
                      "Please manually switch to Plume network in your wallet",
                    type: "error",
                    duration: 5000,
                  });
                }
              },
            },
          });
        }
      }, 100);
    } else {
      // Mark as processed even if no switch needed
      lastProcessedChain.current = chain.id;
    }
  }, [chain, isConnected, location.pathname, switchChain]);

  return {
    isOnCorrectNetwork:
      !isConnected ||
      chain?.id === plumeMainnetId ||
      location.pathname.includes(AppRoute.Bridge),
    currentChain: chain,
    switchToPlume: () => switchChain({ chainId: plumeMainnetId }),
  };
};
